// Retkitukikohta (suunnitelman kohta 10): pienoismaailma, rakennusluvat ja koristeet.

import { useEffect, useState } from "preact/hooks";

import { day } from "../lib/format.js";
import { BaseScene, SEASONS } from "./BaseScene.jsx";

const LINK_LABELS = { diary: "Avaa päiväkirja", pass: "Avaa retkipassi", content: "Avaa retkikohteet", map: "Avaa kartta" };

export function BaseView({ app }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    app.api.base().then(
      (s) => {
        setState(s);
        setError(null);
      },
      (e) => setError(e.message),
    );
  }, [app.dataVersion]);

  if (error) return <div class="page"><div class="page-inner"><h1>Tukikohta</h1><div class="empty"><b>Tukikohtaa ei saatu ladattua</b>{error}</div></div></div>;
  if (!state) return <div class="page"><div class="page-inner"><h1>Tukikohta</h1><p class="s">Ladataan…</p></div></div>;

  const building = state.buildings.find((b) => b.id === selected);
  const builtCount = state.buildings.filter((b) => b.built).length;
  const reducedMotion = document.documentElement.dataset.motion === "reduced" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const build = async (id) => {
    setBusy(true);
    try {
      const next = await app.api.build(id);
      setState(next); // tietopaneeli näyttää heti "Rakennettu", erillistä ilmoitusta ei tarvita
    } catch (e) {
      app.toast({ kind: "info", what: "Rakentaminen ei onnistunut", sub: e.message });
    }
    setBusy(false);
  };

  const toggleDecor = async (d) => {
    try {
      setState(await app.api.setDecor(d.id, !d.visible));
    } catch (e) {
      app.toast({ kind: "info", what: "Muutos ei onnistunut", sub: e.message });
    }
  };

  return (
    <div class="page">
      <div class="page-inner">
        <h1>Tukikohta</h1>
        <p class="lede">
          {SEASONS[state.season]?.label ?? ""}. {builtCount}/{state.buildings.length} rakennusta.{" "}
          {state.slots_free > 0
            ? `${state.slots_free === 1 ? "Yksi rakennuslupa" : `${state.slots_free} rakennuslupaa`} vapaana: valitse tontti.`
            : `Seuraava rakennuslupa tulee tasolta ${state.max_level + 1}.`}
        </p>

        <div class="scene-frame">
          <BaseScene state={state} selected={selected} onSelect={setSelected} reducedMotion={reducedMotion} />
        </div>

        <section class="spot-panel" aria-live="polite">
          {!building && <p class="s">Napauta rakennusta tai tonttia nähdäksesi tiedot.</p>}
          {building && (
            <>
              <h2>{building.name}</h2>
              <p>{building.description}</p>
              {building.built ? (
                <>
                  <p class="s">{building.starter ? "Tukikohdan alku." : `Rakennettu ${day(building.built_at)}.`}</p>
                  {building.link && <button class="btn" onClick={() => app.goTo(building.link)}>{LINK_LABELS[building.link]}</button>}
                </>
              ) : state.slots_free > 0 ? (
                <>
                  <p class="s">Rakennus on pysyvä. Lupa ei katoa, vaikka pisteitä oikaistaisiin myöhemmin.</p>
                  <button class="btn btn-rasti btn-block" disabled={busy} onClick={() => build(building.id)}>Rakenna {building.name.toLowerCase()}</button>
                </>
              ) : (
                <p class="s">Rakennusluvan saa tasolta {state.max_level + 1}, kun pisteitä on {state.next_level_at.toLocaleString("fi-FI")}.</p>
              )}
            </>
          )}
        </section>

        <h2>Rakennukset</h2>
        {state.buildings.map((b) => (
          <button class="item" key={b.id} aria-pressed={selected === b.id} onClick={() => setSelected(b.id)}>
            <svg class="mark" viewBox="0 0 30 30" aria-hidden="true">
              <polygon points="15,4 27,10 27,22 15,28 3,22 3,10" fill={b.built ? "var(--open)" : "none"} stroke={b.built ? "var(--open-ink)" : "var(--line)"} stroke-width="2" stroke-dasharray={b.built ? "none" : "3 3"} />
            </svg>
            <div>
              <div class="t">{b.name}</div>
              <div class="s">{b.built ? (b.starter ? "Tukikohdan alku" : `Rakennettu ${day(b.built_at)}`) : "Rakentamatta"}</div>
            </div>
            <span />
          </button>
        ))}

        <h2>Koristeet</h2>
        <p class="s">Koristeet avautuvat kokoelmista ja virstanpylväistä. Avattu koriste säilyy aina.</p>
        <div class="decor-grid">
          {state.decor.map((d) => (
            <div class={`decor ${d.unlocked ? "open" : ""}`} key={d.id}>
              <b>{d.name}</b>
              <span class="s">{d.unlocked ? `Avattu ${day(d.unlocked_at)}` : d.how}</span>
              {d.unlocked && (
                <label class="check small">
                  <input type="checkbox" checked={d.visible} onChange={() => toggleDecor(d)} />
                  <span>Näkyy tukikohdassa</span>
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
